import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import { useTranslation } from "react-i18next";
import DetailsPageLayout, {
  DetailsPageConfig,
  TableItem,
  UserData,
} from "../../../components/common/DetailsPageLayout";
import { getWithdrawDetails } from "../../../setup/api/services/withdraws";
import {
  IWithdrawalItem,
  IWithdrawDetailsResponseData,
} from "../../../setup/api/services/withdraws/models";
import { getUserDetailsById } from "../../../setup/api/services/user";
import { IUserAllInfoResponseData } from "../../../setup/api/services/user/models";
import { WITHDRAW_STATUS } from "../constants";
import WithdrawModal from "../WithdrawModal";

const WithdrawDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const singleClick = useRef<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [previewItems, setPreviewItems] = useState<IWithdrawalItem[]>([]);

  const [request, setRequest] = useState<IWithdrawDetailsResponseData>();
  const [userData, setUserData] = useState<UserData>();
  const [openWithdrawModal, setOpenWithdrawModal] = useState(false);

  const onCloseWithdrawModal = () => {
    setOpenWithdrawModal(false);
  };

  const fetchWithdrawDetails = useCallback(async () => {
    if (id) {
      try {
        if (page === 1) {
          setPageLoading(true);
        }
        setLoading(true);
        const result = await getWithdrawDetails({
          id,
          page: page - 1,
          size: 10,
        });
        const userResult = await getUserDetailsById(
          result?.data?.beneficiaryId
        );
        setRequest(result.data);
        if (page === 1) {
          setPreviewItems(result.data.withDrawlItems.content.slice(0, 3));
        }
        setPagesCount(result.data.withDrawlItems.totalPages);
        // Map IUserAllInfoResponseData to UserData
        if (userResult?.data) {
          const userDataResponse: IUserAllInfoResponseData = userResult.data;
          setUserData({
            profile: {
              fullName: userDataResponse.profile?.fullName,
              photoUrl: userDataResponse.profile?.photoUrl,
              addresses: userDataResponse.profile?.addresses,
            },
            phoneNumber: userDataResponse.phoneNumber,
            nationalIdVerified: userDataResponse.nationalIdVerified,
          });
        }
      } catch (error) {
        console.error("Error fetching withdraw details:", error);
        setRequest(undefined);
      } finally {
        setPageLoading(false);
        setLoading(false);
      }
    }
  }, [id, page]);

  const handleTransferClick = useCallback(async () => {
    setOpenWithdrawModal(true);
  }, []);

  useEffect(() => {
    fetchWithdrawDetails();
  }, [fetchWithdrawDetails]);

  const handleBackButtonClick = () => {
    const searchParams = new URLSearchParams(location?.search);
    if (searchParams?.get("isOperation") === "true") {
      navigate(ROUTES.COMPLETED_TRANSFERS);
      return;
    }
    navigate(ROUTES.WITHDRAWS);
  };

  const handleContractClick = useCallback(
    (contractId: string) => {
      navigate(`${ROUTES.CONTRACT}${contractId}`);
    },
    [navigate]
  );

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  // Map IWithdrawalItem to TableItem
  const mapToTableItems = useCallback(
    (items: IWithdrawalItem[]): TableItem[] => {
      return items.map((item) => ({
        id: item.contractId || "",
        title: item.title,
        contractId: item.contractId,
        amount: item.amount,
      }));
    },
    []
  );

  const getItemStatus = useCallback(
    (item: TableItem, overallStatus?: string) => {
      return overallStatus === WITHDRAW_STATUS?.COMPLETED
        ? overallStatus
        : WITHDRAW_STATUS?.IN_PROGRESS;
    },
    []
  );

  const config: DetailsPageConfig = {
    transactionContracts: {
      transactionsSectionTitle: "المعاملات",
      items: mapToTableItems(request?.withDrawlItems?.content || []),
      previewItems: mapToTableItems(previewItems),
      tableHeaders: {
        transaction: "المعاملة",
        transactionNumber: "رقم المعاملة",
        totalValue: "القيمة الإجمالية",
        status: "الحالة",
      },
      showAllLinkText: t("lbl_show_child_contracts"),
      onContractClick: handleContractClick,
      getItemStatus: getItemStatus,
      totalPages: pagesCount,
      onPageChange: handlePaginationChange,
      currentPage: page,
    },
    transactionsCountLabel: "عدد المعاملات المرتبطة بالسحب",
    actionButtonText: t("lbl_release"),
    addressErrorText: "لا يوجد عنوان لهذا المستخدم. يجب إضافة عنوان أولاً",
    userData: userData,
    totalAmount: request?.totalAmount,
    referenceNumber: request?.cashOutReferenceNumber || undefined,
    status: request?.status,
    numberOfTransactions: request?.numberOfTransactions,
    onBackClick: handleBackButtonClick,
    onActionButtonClick: handleTransferClick,
    showActionButton:
      request?.status === WITHDRAW_STATUS?.IN_PROGRESS ||
      request?.status === WITHDRAW_STATUS?.FAILED,
    actionButtonDisabled:
      loading || !!singleClick?.current || !userData?.profile?.addresses?.[0],
    showReferenceNumber: !!request?.cashOutReferenceNumber,
    showAddressError:
      !userData?.profile?.addresses?.[0] &&
      request?.status === WITHDRAW_STATUS?.IN_PROGRESS,
    pageLoading: pageLoading,
    actionLoading: loading,
  };

  return (
    <div>
      <DetailsPageLayout {...config} />
      <WithdrawModal
        open={openWithdrawModal}
        onClose={onCloseWithdrawModal}
        paymentAmount={request?.totalAmount || 0}
        userId={request?.beneficiaryId}
        withdrawId={id}
        withdrawDetails={request as IWithdrawDetailsResponseData}
      />
    </div>
  );
};

export default WithdrawDetails;
